"use client"

import * as React from "react"
import Link from "next/link"
import { Header } from "@/components/header"
import { Footer } from "@/components/footer"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Minus, Plus, Trash2, ShoppingBag } from "lucide-react"
import { CartProvider, useCart } from "@/lib/cart-context"

function CartContent() {
  const { items, updateQuantity, removeItem, total, itemCount } = useCart()

  const shipping = total >= 50 ? 0 : 5.99
  const finalTotal = total + shipping

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Your Cart</h1>
              <p className="text-muted-foreground">{itemCount} item{itemCount !== 1 ? 's' : ''}</p>
            </div>
            <Button variant="ghost" asChild>
              <Link href="/merch">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Continue Shopping
              </Link>
            </Button>
          </div>

          {items.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="p-12 text-center">
                <ShoppingBag className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                <h2 className="text-xl font-semibold text-foreground mb-2">Your cart is empty</h2>
                <p className="text-muted-foreground mb-6">Looks like you haven&apos;t added anything yet.</p>
                <Button asChild>
                  <Link href="/merch">Browse Merch</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {items.map((item, index) => (
                  <Card key={`${item.productId}-${item.size}-${item.color}-${index}`} className="bg-card border-border">
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {/* Product Image */}
                        <div className="w-24 h-24 bg-gradient-to-br from-secondary to-muted rounded-lg flex items-center justify-center shrink-0">
                          <span className="text-xl opacity-30">GNL</span>
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-foreground">{item.name}</h3>
                          <div className="text-sm text-muted-foreground mt-1">
                            {item.size && <span>Size: {item.size}</span>}
                            {item.size && item.color && <span> / </span>}
                            {item.color && <span>Color: {item.color}</span>}
                          </div>
                          <p className="text-primary font-bold mt-2">${item.price}</p>
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex flex-col items-end gap-2">
                          <div className="flex items-center border border-border rounded-lg">
                            <button
                              onClick={() => updateQuantity(item.productId, item.quantity - 1, item.size, item.color)}
                              className="p-2 hover:bg-secondary transition-colors"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="px-3 text-sm font-medium">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.productId, item.quantity + 1, item.size, item.color)}
                              className="p-2 hover:bg-secondary transition-colors"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <button
                            onClick={() => removeItem(item.productId, item.size, item.color)}
                            className="text-sm text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
                          >
                            <Trash2 className="h-3 w-3" />
                            Remove
                          </button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Order Summary */}
              <div>
                <Card className="bg-card border-border sticky top-24">
                  <CardContent className="p-6">
                    <h2 className="text-lg font-semibold text-foreground mb-4">Order Summary</h2>
                    
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span className="text-foreground">${total.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Shipping</span>
                        <span className="text-foreground">
                          {shipping === 0 ? (
                            <span className="text-green-500">Free</span>
                          ) : (
                            `$${shipping.toFixed(2)}`
                          )}
                        </span>
                      </div>
                      {shipping > 0 && (
                        <p className="text-xs text-muted-foreground">
                          Add ${(50 - total).toFixed(2)} more for free shipping
                        </p>
                      )}
                      <div className="border-t border-border pt-3 mt-3">
                        <div className="flex justify-between text-base font-semibold">
                          <span className="text-foreground">Total</span>
                          <span className="text-foreground">${finalTotal.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <Button 
                      className="w-full mt-6 bg-primary hover:bg-primary/90"
                      asChild
                    >
                      <Link href="/merch/checkout">Proceed to Checkout</Link>
                    </Button>

                    <p className="mt-4 text-xs text-center text-muted-foreground">
                      Taxes calculated at checkout
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default function CartPage() {
  return (
    <CartProvider>
      <CartContent />
    </CartProvider>
  )
}
